import { useState } from 'react';
import { authApiService } from '../services/auth';
import LoginProgress from './LoginProgress';
import LoginError from './LoginError';

interface SocialLoginButtonsProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  disabled?: boolean;
  role?: string | null;
}

export default function SocialLoginButtons({ onSuccess, onError, disabled = false, role }: SocialLoginButtonsProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [stepMessage, setStepMessage] = useState('');
  const [error, setError] = useState<string>('');
  const [showError, setShowError] = useState(false);

  const handleGoogleSignIn = async () => {
    if (disabled || isLoading) return;
    
    setIsLoading(true);
    setCurrentStep(1);
    setStepMessage('Conectando con Google...');
    
    try {
      setCurrentStep(2);
      setStepMessage('Verificando credenciales...');
      
      const user = await authApiService.signInWithGoogleAndSignUp(role);
      
      if (user) {
        setCurrentStep(3);
        setStepMessage('Creando cuenta en el sistema...');
        
        // Simular paso de configuración
        await new Promise(resolve => setTimeout(resolve, 500));
        
        setCurrentStep(4);
        setStepMessage('Configurando permisos...');
        
        await new Promise(resolve => setTimeout(resolve, 300));
        
        setCurrentStep(5);
        setStepMessage('¡Listo!');
        
        await new Promise(resolve => setTimeout(resolve, 200));
        
        onSuccess?.();
      } else {
        throw new Error('No se pudo obtener información del usuario');
      }
    } catch (error: any) {
      const errorMessage = error.message || 'No se pudo iniciar sesión con Google.';
      setError(errorMessage);
      setShowError(true);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
      setCurrentStep(0);
      setStepMessage('');
    }
  };
  
  // const handleAppleSignIn = async () => {
  //   if (disabled || isLoading) return;
    
  //   setIsLoading(true);
  //   setCurrentStep(1);
  //   setStepMessage('Conectando con Apple...');
    
  //   try {
  //     setCurrentStep(2);
  //     setStepMessage('Verificando credenciales...');
      
  //     const user = await authApiService.signInWithAppleAndSignUp();
      
  //     if (user) {
  //       setCurrentStep(3);
  //       setStepMessage('Creando cuenta en el sistema...');
        
  //       await new Promise(resolve => setTimeout(resolve, 500));
        
  //       setCurrentStep(4);
  //       setStepMessage('Configurando permisos...');
        
  //       await new Promise(resolve => setTimeout(resolve, 300));
        
  //       setCurrentStep(5);
  //       setStepMessage('¡Listo!');
        
  //       await new Promise(resolve => setTimeout(resolve, 200));
        
  //       onSuccess?.();
  //     } else {
  //       throw new Error('No se pudo obtener información del usuario');
  //     }
  //   } catch (error: any) {
  //     const errorMessage = error.message || 'No se pudo iniciar sesión con Apple.';
  //     setError(errorMessage);
  //     setShowError(true);
  //     onError?.(errorMessage);
  //   } finally {
  //     setIsLoading(false);
  //     setCurrentStep(0);
  //     setStepMessage('');
  //   }
  // };

  // const handleFacebookSignIn = async () => {
  //   if (disabled || isLoading) return;
    
  //   setIsLoading(true);
  //   setCurrentStep(1);
  //   setStepMessage('Conectando con Facebook...');
    
  //   try {
  //     setCurrentStep(2);
  //     setStepMessage('Verificando credenciales...');
      
  //     const user = await authApiService.signInWithFacebookAndSignUp();
      
  //     if (user) {
  //       setCurrentStep(3);
  //       setStepMessage('Creando cuenta en el sistema...');
        
  //       await new Promise(resolve => setTimeout(resolve, 500));
        
  //       setCurrentStep(4);
  //       setStepMessage('Configurando permisos...');
        
  //       await new Promise(resolve => setTimeout(resolve, 300));
        
  //       setCurrentStep(5);
  //       setStepMessage('¡Listo!');
        
  //       await new Promise(resolve => setTimeout(resolve, 200));
        
  //       onSuccess?.();
  //     } else {
  //       throw new Error('No se pudo obtener información del usuario');
  //     }
  //   } catch (error: any) {
  //     const errorMessage = error.message || 'No se pudo iniciar sesión con Facebook.';
  //     setError(errorMessage);
  //     setShowError(true);
  //     onError?.(errorMessage);
  //   } finally {
  //     setIsLoading(false);
  //     setCurrentStep(0);
  //     setStepMessage('');
  //   }
  // };

  const renderButton = (
    onPress: () => void,
    icon: string,
    text: string,
    buttonStyle: string,
    textStyle?: string
  ) => (
    <button 
      className={`w-full py-3 rounded-full flex items-center justify-center gap-3 min-h-[50px] font-bold transition-opacity ${
        disabled || isLoading ? 'opacity-60' : ''
      } ${buttonStyle}`}
      onClick={onPress}
      disabled={disabled || isLoading}
    >
      {isLoading ? (
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></div>
      ) : (
        <img 
          src={icon} 
          alt=""
          className="w-5 h-5"
        />
      )}
      <span className={textStyle}>
        {isLoading ? 'Iniciando sesión...' : text}
      </span>
    </button>
  );

  return (
    <>
      <div className="space-y-3">
        {renderButton(
          handleGoogleSignIn,
          'https://www.vectorlogo.zone/logos/google/google-icon.svg',
          'Iniciar con Google',
          'bg-white border border-[#E0E0E0] text-[#4285F4]',
          'text-[#4285F4]'
        )}
        
        {/* {renderButton(
          handleAppleSignIn,
          'https://www.vectorlogo.zone/logos/apple/apple-ar.svg',
          'Iniciar con Apple',
          'bg-black text-white',
          'text-white'
        )}
        
        {renderButton(
          handleFacebookSignIn,
          'https://www.vectorlogo.zone/logos/facebook/facebook-icon.svg',
          'Iniciar con Facebook',
          'bg-[#1877F2] text-white',
          'text-white'
        )} */}
      </div>
      
      {/* Componente de progreso visual */}
      <LoginProgress
        currentStep={currentStep}
        totalSteps={5}
        stepMessage={stepMessage}
        isVisible={isLoading}
      />
      
      {/* Componente de error */}
      <LoginError
        error={error}
        onRetry={() => {
          setShowError(false);
          setError('');
          // Reintentar el último método usado
          if (error.includes('Google')) handleGoogleSignIn();
          // else if (error.includes('Apple')) handleAppleSignIn();
          // else if (error.includes('Facebook')) handleFacebookSignIn();
        }}
        onClose={() => {
          setShowError(false);
          setError('');
        }}
        isVisible={showError}
      />
    </>
  );
} 